import type { ReactNode } from "react";
import U2000CommonFormFooterActions from "./common/U2000CommonFormFooterActions";
import U2000FormNavigationActions from "./common/U2000FormNavigationActions";
import U2000FormStateDebugPanel from "./common/U2000FormStateDebugPanel";
import U2000SaveCancel from "./common/U2000SaveCancel";

import type { U2000FormStateHandlers } from "../../reducer/formReducer/U2000FormStateHandlers";
import type { U2000GridStateHandlers } from "../../reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlers } from "../../reducer/formReducer/U2000SearchStateHandlers";
import { U2000FormStateEnumState } from "../../reducer/formReducer/U2000FormState";
import U2000CommonFormHeaderActions from "./common/U2000CommonFormHeaderActions";
import { useU2000FormLeaveGuard } from "../../hooks/useU2000FormLeaveGuard";

interface U2000DefaultFormProps<T extends object> {
  actionsBtns: ReactNode;
  searchStateHandlers: U2000SearchStateHandlers;
  gridStateHandlers: U2000GridStateHandlers<T>;
  formStateHandlers: U2000FormStateHandlers<T>;
  children: ReactNode;
  /** Affiche le panneau flottant d'état du formulaire. Défaut: true en dev sauf si VITE_SHOW_FORM_STATE_DEBUG=false. */
  showFormStateDebug?: boolean;
  /**
   * Remplit la hauteur disponible du workspace (chaîne flex min-h-0) : le corps du formulaire s’étire et le défilement
   * peut être confiné aux enfants (ex. tableau d’honoraires).
   */
  fillMainContentHeight?: boolean;
  /** Affiche le bouton retour liste (flèche) dans U2000FormNavigationActions. Défaut true. BL-0430 : false sur U3148 fiche org. */
  showBackToList?: boolean;
  /** Contenu à droite de la navigation fiche (ex. toggle week-end U3129 — BL-0672). */
  headerNavTrailing?: ReactNode;
  /** En édition, ne pas afficher Enregistrer/Annuler en en-tête (conserver le pied). BL-0672 U3129. */
  hideHeaderSaveCancelInEditMode?: boolean;
  /** En édition, masquer la barre d’actions en-tête (`actionsBtns`, ex. Ajouter / Modifier U3129). BL-0672. */
  hideHeaderToolbarInEditMode?: boolean;
}

const defaultShowFormStateDebug =
  import.meta.env.DEV && import.meta.env.VITE_SHOW_FORM_STATE_DEBUG !== "false";

function U2000DefaultForm<T extends object>({
  actionsBtns,
  gridStateHandlers,
  formStateHandlers,
  children,
  showFormStateDebug = defaultShowFormStateDebug,
  fillMainContentHeight = false,
  showBackToList = true,
  headerNavTrailing,
  hideHeaderSaveCancelInEditMode = false,
  hideHeaderToolbarInEditMode = false,
}: U2000DefaultFormProps<T>) {
  const formState = formStateHandlers.getFormState();
  const isEditMode = formState === U2000FormStateEnumState.EDIT;
  useU2000FormLeaveGuard(formStateHandlers);

  const showHeaderToolbar = Boolean(actionsBtns) && !(isEditMode && hideHeaderToolbarInEditMode);
  const showHeaderNav =
    Boolean(gridStateHandlers) &&
    (isEditMode ? !hideHeaderSaveCancelInEditMode || Boolean(headerNavTrailing) : true);
  const showFormCardHeader = showHeaderToolbar || showHeaderNav;

  return (
    <>
      <main
        className={`w-full px-4 sm:px-6 lg:px-8 py-2 ${fillMainContentHeight ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" : ""}`}
      >
        <article
          className={`form-card animate-fade-in w-full ${fillMainContentHeight ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" : ""}`}
          role="main"
          aria-label="Formulaire principal"
        >
          {/* Header Section */}
          {showFormCardHeader ? (
            <header className={`form-card-header ${fillMainContentHeight ? "shrink-0" : ""}`}>
              <div className="flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between">
                {showHeaderToolbar ? (
                  <section className="flex-1" aria-label="Actions du formulaire">
                    <U2000CommonFormHeaderActions actionsButtons={actionsBtns} />
                  </section>
                ) : null}

                {showHeaderNav ? (
                  isEditMode ? (
                    hideHeaderSaveCancelInEditMode ? (
                      headerNavTrailing ? (
                        <div className="shrink-0 lg:ml-6 flex flex-wrap items-center justify-end gap-2">{headerNavTrailing}</div>
                      ) : null
                    ) : (
                      <div className="shrink-0 lg:ml-6 flex items-center gap-2" role="toolbar" aria-label="Actions de sauvegarde">
                        <U2000SaveCancel formStateHandlers={formStateHandlers} />
                      </div>
                    )
                  ) : (
                    <nav className="shrink-0 lg:ml-6 flex flex-wrap items-center justify-end gap-2" aria-label="Navigation des enregistrements">
                      <U2000FormNavigationActions
                        formStateHandlers={formStateHandlers}
                        gridStateHandlers={gridStateHandlers!}
                        onRecordChange={formStateHandlers.onRecordChange}
                        onPageChange={formStateHandlers.onPageChange}
                        handleGoBackFn={showBackToList ? formStateHandlers.onLeave : undefined}
                      />
                      {headerNavTrailing}
                    </nav>
                  )
                ) : null}
              </div>
            </header>
          ) : null}

          {/* Main Content Section */}
          <section
            className={`form-card-body w-full ${fillMainContentHeight ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" : ""}`}
            aria-label="Contenu du formulaire"
          >
            {children}
          </section>

          {/* Footer Section */}
          <footer className={`form-card-footer ${fillMainContentHeight ? "shrink-0" : ""}`} aria-label="Actions de sauvegarde">
            <U2000CommonFormFooterActions formStateHandlers={formStateHandlers} />
          </footer>
        </article>
      </main>
      {showFormStateDebug && <U2000FormStateDebugPanel formStateHandlers={formStateHandlers} />}
    </>
  );
}

export default U2000DefaultForm;
